import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import {
  DocumentationFileWithContent,
  DocumentationFileWithContentDTO,
  DocumentRequest,
  DocumentRequestCreatedDTO,
  DocumentRequestDTO,
  DocumentRequestFilter,
  DocumentRequestsCountDTO,
  DocumentRequestStatusChangedDTO,
  DocumentsRequestHistoryEntryDTO,
  DocumentsRequestPageEntryDTO,
  EmailTemplate,
  EmailTemplateDTO,
  EmailTemplateLabel,
  EmailTemplatesLabelsDTO,
  RecategoriseFile,
  RejectDocumentationDTO,
  RenameFileDTO,
  RequestDocumentsDTO,
} from './documents.model';
import { HttpClient, HttpEvent, HttpHeaders, HttpParams, HttpRequest } from '@angular/common/http';
import { Page } from '../../../../common/paging-dto';
import { map } from 'rxjs/operators';
import { Sort } from '@angular/material/sort';
import { PlayerWsService } from '../../../../common/services/ws/player-service/player-ws.service';

@Injectable({
  providedIn: 'root',
})
export class DocumentsService {
  private playerWsService = inject(PlayerWsService);

  constructor(private http: HttpClient) {}

  public getFilteredPage(
    pageNumber: number,
    pageSize: number,
    filters: DocumentRequestFilter,
    sort: Sort
  ): Observable<Page<DocumentsRequestPageEntryDTO>> {
    let params = new HttpParams().set('pageNumber', pageNumber.toString()).set('pageSize', pageSize.toString());

    params = this.mapFiltersToParams(filters, params);

    if (sort) {
      params = params.append('sort-field', sort.active);
      params = params.append('sort-direction', sort.direction.toUpperCase());
    }

    return this.http.get<Page<DocumentsRequestPageEntryDTO>>(`/player/api/backoffice/documents-requests`, { params });
  }

  public getDocReqUpdatedObs(): Observable<DocumentRequestDTO> {
    return this.playerWsService.docRequestUpdatedObservable();
  }

  public requestDocuments(playerId: string, req: RequestDocumentsDTO): Observable<void> {
    return this.http.post<void>(`/player/api/backoffice/players/${playerId}/documents-requests`, req);
  }

  public cancelDocumentRequest(playerId: number, id: number, note: string = ''): Observable<void> {
    return this.http.post<void>(`/player/api/backoffice/players/${playerId}/documents-requests/${id}/cancel`, { note });
  }

  public cancelDocumentRequestAndSaveFiles(playerId: number, id: number, note: string): Observable<void> {
    return this.http.post<void>(`/player/api/backoffice/players/${playerId}/documents-requests/${id}/cancel-archive`, {
      note,
    });
  }

  public completeDocumentRequest(playerId: number, id: number, note: string): Observable<void> {
    return this.http.post<void>(`/player/api/backoffice/players/${playerId}/documents-requests/${id}/complete`, {
      note,
    });
  }

  public markDocumentRequestFraud(playerId: number, id: number, note: string): Observable<void> {
    return this.http.post<void>(`/player/api/backoffice/players/${playerId}/documents-requests/${id}/fraud-complete`, {
      note,
    });
  }

  public reopenDocumentRequest(playerId: number, id: number, note: string): Observable<void> {
    return this.http.post<void>(`/player/api/backoffice/players/${playerId}/documents-requests/${id}/reopen`, { note });
  }

  public forceDocumentsRequestReview(playerId: number, id: number, note: string = ''): Observable<void> {
    return this.http.post<void>(`/player/api/backoffice/players/${playerId}/documents-requests/${id}/force-review`, {
      note,
    });
  }

  public getDocumentsRequest(playerId: number, id: number): Observable<DocumentRequest> {
    return this.http.get<DocumentRequestDTO>(`/player/api/backoffice/players/${playerId}/documents-requests/${id}`);
  }

  public getDocumentsRequestHistory(playerId: number, id: number): Observable<DocumentsRequestHistoryEntryDTO[]> {
    return this.http.get<DocumentsRequestHistoryEntryDTO[]>(
      `/player/api/backoffice/players/${playerId}/documents-requests/${id}/history`
    );
  }

  public uploadFile(
    playerId: number,
    docReqId: number,
    category: string,
    cardCategoryId: number | undefined,
    documentation: string,
    file: File
  ): Observable<HttpEvent<any>> {
    let url = `/player/api/backoffice/players/${playerId}/documents-requests/${docReqId}/files?category=${category}&documentation=${documentation}`;
    if (cardCategoryId) {
      url = `${url}&cardVerificationRequestId=${cardCategoryId}`;
    }

    let formData = new FormData();
    formData.append('file', file, file.name);

    const headers = new HttpHeaders({
      'Content-Type': 'multipart/form-data',
    });

    const req = new HttpRequest('POST', url, formData, {
      headers: headers,
      reportProgress: true,
      params: new HttpParams(),
    });
    return this.http.request(req);
  }

  public updateFileContent(
    playerId: number,
    docReqId: number,
    fileId: number,
    blob: Blob,
    newExtension: string
  ): Observable<HttpEvent<any>> {
    let url = `/player/api/backoffice/players/${playerId}/documents-requests/${docReqId}/files/${fileId}/update`;
    if (newExtension) {
      url = url + `?newType=${newExtension}`;
    }

    let formData = new FormData();
    formData.append('file', blob);

    const headers = new HttpHeaders({
      'Content-Type': 'multipart/form-data',
    });

    const req = new HttpRequest('POST', url, formData, {
      headers: headers,
      reportProgress: true,
      params: new HttpParams(),
    });
    return this.http.request(req);
  }

  public acceptFile(playerId: number, docRequestId: number, fileId: number): Observable<void> {
    return this.http.post<void>(
      `/player/api/backoffice/players/${playerId}/documents-requests/${docRequestId}/files/${fileId}/accept`,
      {}
    );
  }

  public rejectFile(playerId: number, docRequestId: number, fileId: number): Observable<void> {
    return this.http.post<void>(
      `/player/api/backoffice/players/${playerId}/documents-requests/${docRequestId}/files/${fileId}/reject`,
      {}
    );
  }

  public acceptDocumentation(playerId: number, docRequestId: number, documentationId: number): Observable<void> {
    return this.http.post<void>(
      `/player/api/backoffice/players/${playerId}/documents-requests/${docRequestId}/documentation/${documentationId}/accept`,
      {}
    );
  }

  public rejectDocumentation(
    playerId: number,
    docRequestId: number,
    documentationId: number,
    rejectionComment: string,
    rejectWholeDocumentation: boolean
  ): Observable<void> {
    const dto: RejectDocumentationDTO = {
      rejectionComment,
      rejectWholeDocumentation,
    };
    return this.http.post<void>(
      `/player/api/backoffice/players/${playerId}/documents-requests/${docRequestId}/documentation/${documentationId}/reject`,
      dto
    );
  }

  public downloadFileContent(
    playerId: number,
    docRequestId: number,
    fileId: number
  ): Observable<DocumentationFileWithContent> {
    return this.http.get<DocumentationFileWithContentDTO>(
      `/player/api/backoffice/players/${playerId}/documents-requests/${docRequestId}/files/${fileId}`
    );
  }

  public downloadFileAsJpg(
    playerId: number,
    docRequestId: number,
    fileId: number
  ): Observable<DocumentationFileWithContent> {
    return this.http.get<DocumentationFileWithContentDTO>(
      `/player/api/backoffice/players/${playerId}/documents-requests/${docRequestId}/files/${fileId}/as-jpg`
    );
  }

  public getEmailTemplates(): Observable<EmailTemplateLabel[]> {
    return this.http
      .get<EmailTemplatesLabelsDTO>(`/player/api/backoffice/documents-requests/email-templates`)
      .pipe(map((e) => e.labels));
  }

  public getEmailTemplateContent(id: string, firstName: string, cards: string[]): Observable<EmailTemplate> {
    return this.http.get<EmailTemplateDTO>(`/player/api/backoffice/documents-requests/email-templates/${id}`, {
      params: { firstName, cards },
    });
  }

  public renameFile(playerId: number, docRequestId: number, fileId: number, newName: string): Observable<void> {
    const body: RenameFileDTO = { newName };
    return this.http.post<void>(
      `/player/api/backoffice/players/${playerId}/documents-requests/${docRequestId}/files/${fileId}/rename`,
      body
    );
  }

  removeDocumentationFile(playerId: number, docRequestId: number, id: number) {
    return this.http.delete<void>(
      `/player/api/backoffice/players/${playerId}/documents-requests/${docRequestId}/files/${id}`
    );
  }

  public recategoriseFile(recategoriseFile: RecategoriseFile): Observable<void> {
    return this.http.post<void>(
      `/player/api/backoffice/players/${recategoriseFile.playerId}/documents-requests/${recategoriseFile.docRequestId}/files/${recategoriseFile.fileId}/recategorise`,
      recategoriseFile
    );
  }

  runFullKyc(playerId: number): Observable<void> {
    return this.http.post<void>(`/player/api/backoffice/players/${playerId}/kyc`, {});
  }

  public getDocumentsRequestsCount(filters: DocumentRequestFilter): Observable<DocumentRequestsCountDTO> {
    let params = this.mapFiltersToParams(filters, new HttpParams());
    return this.http.get<DocumentRequestsCountDTO>('/player/api/backoffice/documents-requests/count', { params });
  }

  private mapFiltersToParams(filters: DocumentRequestFilter, params: HttpParams): HttpParams {
    if (filters?.playerId) {
      params = params.append('playerId', filters.playerId);
    }
    if (filters?.requestor) {
      params = params.append('requestor', filters.requestor);
    }
    if (filters?.categories && filters?.categories.length > 0) {
      params = params.append('category', filters.categories.map((cat) => cat.toString()).toString());
    }
    if (filters?.status) {
      params = params.append('status', filters.status);
    }
    if (filters?.zendeskId) {
      params = params.append('zendeskId', filters.zendeskId);
    }
    if (filters?.lockedBy) {
      params = params.append('lockedBy', filters.lockedBy);
    }
    return params;
  }

  public docRequestStatusObservable(): Observable<DocumentRequestStatusChangedDTO> {
    return this.playerWsService.docRequestStatusObservable();
  }

  public docRequestUpdatedObservable(): Observable<DocumentRequestDTO> {
    return this.playerWsService.docRequestUpdatedObservable();
  }

  public docRequestCreatedObservable(): Observable<DocumentRequestCreatedDTO> {
    return this.playerWsService.docRequestCreatedObservable();
  }

  public lockDocReq(docId: number): Observable<any> {
    return this.http.post<any>(`/player/api/backoffice/documents-requests/${docId}/lock`, {});
  }

  public unlockDockReq(docId: number): Observable<any> {
    return this.http.post<any>(`/player/api/backoffice/documents-requests/${docId}/unlock`, {});
  }

  public getActiveDocRequestsForPlayer(playerId: string): Observable<DocumentRequestDTO[]> {
    return this.http.get<DocumentRequestDTO[]>(`/player/api/backoffice/documents-requests/active`, {
      params: { playerId: playerId },
    });
  }
}
